import { Component, Inject, Input, signal, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ProjectAttachmentService } from './project-attachment.service';
import { Subscription } from 'rxjs';
import { CONFIRM_SERVICE, IConfirmService } from '../../../core';

interface AttachmentDisplay {
	name: string;
	contentType: string;
	size: number;
	uploadedAt?: string;
	thumbnailUrl?: string;
	loading?: boolean;
	error?: boolean;
}

@Component({
	selector: 'bp-project-attachments',
	standalone: true,
	imports: [CommonModule],
	templateUrl: './attachments-list.component.html',
	styleUrls: ['./attachments-list.component.css']
})
export class AttachmentsListComponent implements OnInit, OnDestroy {
	@Input() projectId!: string;
	@Input() readonly = false;

	attachments = signal<AttachmentDisplay[]>([]);
	isUploading = signal<boolean>(false);
	isLoading = signal<boolean>(true);

	private subscriptions: Subscription[] = [];

	constructor(
		private pas: ProjectAttachmentService,
		private cdr: ChangeDetectorRef,
		@Inject(CONFIRM_SERVICE) private confirmService: IConfirmService
	) {}

	async ngOnInit(): Promise<void> {
		// Auf Änderungen reagieren
		this.subscriptions.push(
			this.pas.attachmentsChanged.subscribe(() => this.loadAttachments())
		);
		await this.loadAttachments();
	}

	ngOnDestroy(): void {
		this.subscriptions.forEach(s => s.unsubscribe());
		// Object URLs freigeben
		this.attachments().forEach(a => {
			if (a.thumbnailUrl && a.thumbnailUrl.startsWith('blob:')) {
				URL.revokeObjectURL(a.thumbnailUrl);
			}
		});
	}

	private async loadAttachments(): Promise<void> {
		this.isLoading.set(true);
		try {
			const meta = await this.pas.getAttachmentsMeta(this.projectId);
			const displayItems: AttachmentDisplay[] = meta.map(m => ({
				...m,
				thumbnailUrl: undefined,
				loading: false,
				error: false
			}));
			this.attachments.set(displayItems);

			// Thumbnails für Bilder generieren
			await this.loadThumbnails();
		} finally {
			this.isLoading.set(false);
		}
	}

	private async loadThumbnails(): Promise<void> {
		const items = this.attachments();
		for (let i = 0; i < items.length; i++) {
			const item = items[i];
			if (this.pas.isImage(item.contentType)) {
				try {
					const blob = await this.pas.getAttachment(this.projectId, item.name);
					if (blob) {
						const thumbnailUrl = await this.pas.generateThumbnail(blob, 100);
						// Update einzelnes Item
						const updated = [...this.attachments()];
						if (updated[i]) {
							updated[i] = { ...updated[i], thumbnailUrl };
							this.attachments.set(updated);
						}
					}
				} catch (e) {
					console.error('Fehler beim Laden des Thumbnails:', e);
					const updated = [...this.attachments()];
					if (updated[i]) {
						updated[i] = { ...updated[i], error: true };
						this.attachments.set(updated);
					}
				}
			}
		}
	}

	async onFileSelected(event: Event): Promise<void> {
		const input = event.target as HTMLInputElement;
		if (!input.files || input.files.length === 0) return;

		this.isUploading.set(true);
		try {
			for (const file of Array.from(input.files)) {
				await this.pas.uploadAttachment(this.projectId, file);
			}
		} finally {
			this.isUploading.set(false);
			input.value = ''; // Input zurücksetzen für erneute Auswahl
		}
	}

	async onAttachmentClick(attachment: AttachmentDisplay): Promise<void> {
		// Bereits ladend?
		if (attachment.loading) return;

		// Loading-Status setzen
		const items = this.attachments();
		const idx = items.findIndex(a => a.name === attachment.name);
		if (idx >= 0) {
			const updated = [...items];
			updated[idx] = { ...updated[idx], loading: true };
			this.attachments.set(updated);
		}

		try {
			const blob = await this.pas.getAttachment(this.projectId, attachment.name);
			if (blob) {
				// Datei zum Download/Anzeige öffnen
				const url = URL.createObjectURL(blob);
				window.open(url, '_blank');
				// URL nach kurzer Zeit freigeben
				setTimeout(() => URL.revokeObjectURL(url), 60000);
			}
		} catch (e) {
			console.error('Fehler beim Laden des Attachments:', e);
			alert('Fehler beim Laden der Datei.');
		} finally {
			// Loading-Status entfernen
			const items = this.attachments();
			const idx = items.findIndex(a => a.name === attachment.name);
			if (idx >= 0) {
				const updated = [...items];
				updated[idx] = { ...updated[idx], loading: false };
				this.attachments.set(updated);
			}
		}
	}

	async onDelete(attachment: AttachmentDisplay, event: Event): Promise<void> {
		event.stopPropagation();
		const confirmed = await this.confirmService.confirmDelete(`"${attachment.name}" wirklich löschen?`);
		if (!confirmed) return;

		const success = await this.pas.deleteAttachment(this.projectId, attachment.name);
		if (!success) {
			await this.confirmService.error('Fehler beim Löschen der Datei.');
		}
	}

	formatFileSize(bytes: number): string {
		if (bytes < 1024) return bytes + ' B';
		if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
		return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
	}

	getAcceptString(): string {
		return '.jpg,.jpeg,.png,.bmp,.tif,.tiff,.pdf';
	}

	isImage(contentType: string): boolean {
		return this.pas.isImage(contentType);
	}

	isPdf(contentType: string): boolean {
		return this.pas.isPdf(contentType);
	}
}
